import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Award, Clock, Play, FileQuestion, Download } from 'lucide-react';
import { certificationService, CertificationCourse } from '@/features/certification/services/certification.service';
import { usePermissions } from '@/common/hooks/usePermissions';
import { Permission } from '@/common/enums/permissions.enum';

export const Route = createFileRoute('/_authenticated/certification/')({
  component: CertificationListPage,
});

function CertificationListPage() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canView = hasPermission(Permission.CERTIFICATION_VIEW);

  const { data: courses = [], isLoading } = useQuery({
    queryKey: ['certification', 'courses'],
    queryFn: () => certificationService.listPublished(),
    enabled: canView,
  });

  const progressQueries = useQuery({
    queryKey: ['certification', 'progress', courses.map((c) => c.id)],
    queryFn: async () => {
      const map: Record<string, number> = {};
      await Promise.all(
        courses.map(async (c) => {
          try {
            const p = await certificationService.getProgress(c.id);
            map[c.id] = p.progressPct;
          } catch {
            map[c.id] = 0;
          }
        }),
      );
      return map;
    },
    enabled: canView && courses.length > 0,
  });
  const progressMap = progressQueries.data ?? {};

  // Buscar certificados para cursos completos
  const certificateQueries = useQuery({
    queryKey: ['certification', 'certificates', courses.map((c) => c.id)],
    queryFn: async () => {
      const map: Record<string, { id: string; downloadUrl?: string } | null> = {};
      await Promise.all(
        courses.map(async (c) => {
          try {
            // Só buscar certificado se o curso estiver 100% completo
            if (progressMap[c.id] >= 100) {
              const cert = await certificationService.getCertificateByCourse(c.id);
              // Verificar se o certificado realmente existe (tem id)
              if (cert && cert.id) {
                map[c.id] = cert;
              } else {
                map[c.id] = null;
              }
            } else {
              map[c.id] = null;
            }
          } catch {
            // Se a API retornar 404 ou erro, não há certificado
            map[c.id] = null;
          }
        }),
      );
      return map;
    },
    enabled: canView && courses.length > 0 && Object.keys(progressMap).length > 0,
  });
  const certificateMap = certificateQueries.data ?? {};

  if (!canView) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Você não tem permissão para acessar a área de certificação.</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Certificação</h1>
        <p className="text-muted-foreground">Cursos disponíveis para certificação.</p>
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <div className="h-32 bg-muted rounded-t-lg" />
              <CardHeader className="h-6 w-3/4 bg-muted rounded" />
              <CardContent className="space-y-2">
                <div className="h-4 bg-muted rounded w-1/2" />
                <div className="h-4 bg-muted rounded w-1/3" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : courses.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Award className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Nenhum curso publicado no momento.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {courses.map((course) => (
            <CourseCard
              key={course.id}
              course={course}
              progressPct={progressMap[course.id] ?? 0}
              certificate={certificateMap[course.id]}
              navigate={navigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CourseCard({
  course,
  progressPct,
  certificate,
  navigate,
}: {
  course: CertificationCourse;
  progressPct: number;
  certificate?: { id: string; downloadUrl?: string } | null;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const coverUrl = course.coverUrl || (course.coverStorageKey ? undefined : undefined);
  const courseCompleted = progressPct >= 100;
  const hasCertificate = !!(certificate && certificate.id);
  
  console.log('[CourseCard] Course:', course.title, {
    courseCompleted,
    certificate,
    hasCertificate,
  });

  const openCourse = () => {
    navigate({ to: '/certification/courses/$courseId', params: { courseId: course.id } });
  };

  const openSimulator = () => {
    navigate({
      to: '/certification/courses/$courseId/simulado',
      params: { courseId: course.id },
    });
  };

  const downloadCertificate = () => {
    if (certificate?.downloadUrl) {
      window.open(certificate.downloadUrl, '_blank');
    } else if (certificate?.id) {
      navigate({
        to: '/certification/certificados/$certificateId',
        params: { certificateId: certificate.id },
      });
    }
  };

  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow">
      <div className="aspect-video bg-muted relative">
        {coverUrl ? (
          <img src={coverUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Award className="h-12 w-12 text-muted-foreground" />
          </div>
        )}
      </div>
      <CardHeader>
        <h3 className="font-semibold line-clamp-2">{course.title}</h3>
        <div className="flex items-center justify-between gap-2">
          {course.estimatedHours != null && (
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              {course.estimatedHours}h
            </div>
          )}
          {progressPct > 0 && (
            <span className="text-xs text-muted-foreground">{progressPct}%</span>
          )}
        </div>
        {progressPct > 0 && (
          <Progress value={progressPct} className="h-1.5" />
        )}
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        {!courseCompleted ? (
          <Button onClick={openCourse} className="w-full" size="sm">
            <Play className="h-4 w-4 mr-2" />
            {progressPct > 0 ? 'Continuar' : 'Iniciar certificação'}
          </Button>
        ) : (
          <>
            <Button onClick={openCourse} variant="outline" className="w-full" size="sm">
              <Play className="h-4 w-4 mr-2" />
              Ver curso
            </Button>
            {hasCertificate ? (
              <Button onClick={downloadCertificate} className="w-full" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Baixar certificado
              </Button>
            ) : (
              <Button onClick={openSimulator} className="w-full" size="sm">
                <FileQuestion className="h-4 w-4 mr-2" />
                Fazer simulado
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
